import { Component, Host, h, Element, Prop, Watch } from '@stencil/core';
import {
  hideIfSlotEmpty,
  hideHostIfSlotEmpty,
  originalHostHiddenState,
} from '../../utils/slotHelpers';
import { toggleDisableChildNodes } from '../../utils/toggleDisableChildNodes';

export interface AhaMenuGroup extends HTMLAhaMenuGroupElement {}

/**
 * A largely presentational component that groups associated `aha-menu-item` components. Think of `<aha-menu-group>` as a nested `<ul>`.
 *
 * Allows for an optional label.
 *
 * Has the ability to be disabled, which in turn will disable all child elements.
 *
 * Automatically draws dividing lines between sections.
 *
 * @slot DEFAULT - Contents that appear as the "body" of a menu-group.
 * @slot label - Contents that appear above the "body" of a menu-group, which briefly describe the function of the grouping.
 */
@Component({
  tag: 'aha-menu-group',
  styleUrl: 'aha-menu-group.css',
  shadow: true,
})
export class AhaMenuGroup {
  @Element() host!: HTMLAhaMenuGroupElement;

  /** _DEPRECATED:_ The plain-text label for the menu group. */
  @Prop() label?: string | undefined;
  @Watch('label')
  private deprecatedGenerateLabel() {
    if (this.label) {
      let label = this.host.querySelector<HTMLElement>('[slot="label"]');
      if (!label) {
        label = document.createElement('div');
        label.slot = 'label';
        this.host.insertAdjacentElement('afterbegin', label);
      }
      label.innerText = this.label;
      this.host.removeAttribute('label');
    }
  }

  /** Whether or not the menu-group is disabled. */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** Whether or not the entire menu-group should be hidden when it has no content in its default slot. By default the menu-group **will be hidden** if there are no contents in the default slot. */
  @Prop({ reflect: true, attribute: 'show-when-empty' }) showWhenEmpty: boolean = false;
  @Watch('showWhenEmpty')
  showOrHideElement() {
    if (this.showWhenEmpty) {
      this.host.hidden = originalHostHiddenState.get(this.host);
      originalHostHiddenState.delete(this.host);
    } else {
      const slot = this.host.shadowRoot?.querySelector('slot:not([name])') as HTMLSlotElement;
      if (slot) hideHostIfSlotEmpty(slot);
    }
  }

  @Watch('disabled')
  disableChildren() {
    toggleDisableChildNodes(this.host);
  }

  connectedCallback() {
    if (this.label) this.deprecatedGenerateLabel();
  }

  componentDidLoad() {
    if (this.disabled) this.disableChildren();
    this.handleSlotChange();
  }

  private labelSlot!: HTMLSlotElement;

  // If the "label" slot is empty, then hide the slot. This ensures that we don't have extra
  // and undesired vertical space.
  private handleSlotChange() {
    hideIfSlotEmpty(this.labelSlot);
  }

  private handleDefaultSlotEmpty(event: any) {
    if (!this.showWhenEmpty) {
      hideHostIfSlotEmpty(event.target);
    }
  }

  render() {
    return (
      <Host role="group">
        <slot
          name="label"
          ref={ref => (this.labelSlot = ref as HTMLSlotElement)}
          onSlotchange={this.handleSlotChange}
        />
        <div role="list">
          <slot onSlotchange={this.handleDefaultSlotEmpty}></slot>
        </div>
      </Host>
    );
  }
}
